import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress
} from '@mui/material'
import { styled } from '@mui/material/styles'
import moment from 'moment'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import { makeStyles } from '@mui/styles'

import { useEventComments } from '../../hooks/useEventComments'
import { useSidebarParam } from '../../hooks/useSidebarParam'
import { eventDetail } from '../../services/EventService'
import {
  SIDEBAR_DRAWER_WIDTH,
  closeSidebarParam,
  notifyEventCommentsUpdated
} from '../../utils/sidebarUrl'
import { canUserAccessEventComments } from '../../utils/eventCommentsAccess'
import { EventComment, Role, User } from '../../services/Types'

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: SIDEBAR_DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: SIDEBAR_DRAWER_WIDTH,
    boxSizing: 'border-box',
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column'
  },
}))

const useStyles = makeStyles({
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: '#f5f5f5',
    flexShrink: 0
  },
  drawerContent: {
    flex: 1,
    padding: '16px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'inherit'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#D9D9D9',
      borderRadius: '1.25rem'
    }
  },
  inputArea: {
    padding: '16px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: '#f5f5f5',
    flexShrink: 0
  }
})

const getRoleLabel = (comment: EventComment): string => {
  if (comment.user.role === Role.ADMIN) return 'Admin'
  if (comment.user.role === Role.APPROVER) {
    return `Approver (L${comment.user.approverLevel})`
  }
  return 'Requestor'
}

export const EventCommentsDrawer: React.FC = () => {
  const classes = useStyles()
  const { isOpen, eventId } = useSidebarParam('comments')
  const [eventData, setEventData] = useState<{ title?: string }>({})
  const [message, setMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}') as User

  const canAccess = canUserAccessEventComments(currentUser)
  const { comments, loading, error, sending, sendComment } = useEventComments(
    canAccess && isOpen ? eventId : 0
  )

  useEffect(() => {
    if (isOpen && !canAccess) {
      closeSidebarParam('comments')
    }
  }, [isOpen, canAccess])

  useEffect(() => {
    if (!eventId || !canAccess) return

    const loadEventData = async () => {
      try {
        const { data } = await eventDetail(eventId)
        if (data?.eventDetail?.statusCode === 200) {
          setEventData(data.eventDetail.data)
        }
      } catch (err) {
        console.log(err)
      }
    }

    loadEventData()
  }, [eventId, canAccess])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' })
  }, [comments, loading])

  const handleClose = () => {
    setMessage('')
    closeSidebarParam('comments')
  }

  const handleSend = async () => {
    if (!message.trim() || sending) return
    const success = await sendComment(message)
    if (success) {
      setMessage('')
      notifyEventCommentsUpdated(eventId)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!isOpen || !canAccess) return null

  const isOwnMessage = (comment: EventComment) => comment.user.id === currentUser.id

  return (
    <StyledDrawer
      variant="persistent"
      anchor="right"
      open={isOpen}
      sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100vh',
        zIndex: 1200
      }}
    >
      <Box className={classes.drawerHeader}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Event Comments
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.12)', backgroundColor: '#fff8e1' }}>
        <Typography variant="body2" sx={{ color: '#5d4037', fontStyle: 'italic' }}>
          (Usage: These comments are internal to Approver2 and Approver3 only.)
        </Typography>
      </Box>

      {eventData.title && (
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <Typography variant="subtitle2" sx={{ color: '#79747E', fontWeight: 500 }}>
            {eventData.title}
          </Typography>
        </Box>
      )}

      <Box className={classes.drawerContent}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && !loading && (
          <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        {!loading && comments.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No comments yet. Start the conversation below.
          </Typography>
        )}

        {comments.map(comment => (
          <Box
            key={comment.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isOwnMessage(comment) ? 'flex-end' : 'flex-start',
              maxWidth: '100%'
            }}
          >
            <Box
              sx={{
                maxWidth: '85%',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                backgroundColor: isOwnMessage(comment) ? '#e3f2fd' : '#f5f5f5',
                border: isOwnMessage(comment) ? '1px solid #bbdefb' : '1px solid #e0e0e0'
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                {comment.user.firstName} {comment.user.lastName}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({getRoleLabel(comment)})
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}>
                {comment.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {moment(Number(comment.createdAt)).format('MM/DD/YYYY hh:mm A')}
              </Typography>
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      <Box className={classes.inputArea}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Type a message..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
          size="small"
          sx={{ mb: 1, backgroundColor: 'white' }}
        />
        <Button
          variant="contained"
          endIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !message.trim()}
          fullWidth
          sx={{ textTransform: 'none' }}
        >
          Send
        </Button>
      </Box>
    </StyledDrawer>
  )
}
